import { z } from 'zod';
import type { HTMLAttributes } from 'react';
import React from 'react';
import classNames from 'classnames';
import { merge } from 'lodash';

import { Card } from '$src-components/atoms/Card';

import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { Link } from '$quantum/atoms/Link';
import { CmsLinkSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import { ContentComponentSection } from '$src-components/atoms/ContentComponentSection';
import { Col, Row } from '$src-components/atoms/Grid';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import { carMakesList } from './fixtures';

import styles from './index.module.scss';

const defaultConfiguration = {
  showAllMakes: false,
};

const CarMakesListSchema = {
  translatedEntities: z.object({
    title: CmsShortTextSchema,
    viewAllLink: CmsLinkSchema,
  }),
  configuration: z
    .object({
      showAllMakes: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type CarMakesListTranslatedEntities = SchemaType<(typeof CarMakesListSchema)['translatedEntities']>;
export type CarMakesListConfiguration = SchemaType<(typeof CarMakesListSchema)['configuration']>;

export type CarMakesListProps = HTMLAttributes<HTMLDivElement>;

export const CarMakesList: ContentComponent<
  CarMakesListTranslatedEntities,
  CarMakesListConfiguration,
  CarMakesListProps
> = function CarMakesList({ translatedEntities, configuration, className, ...props }): JSX.Element {
  const { title, viewAllLink } = translatedEntities;
  const { showAllMakes } = merge(defaultConfiguration, configuration);

  return (
    <ContentComponentSection>
      <Row className={styles.CarMakesListWrapper}>
        <Col>
          <Card
            cornerRadius={4}
            className={classNames(CarMakesList.displayName, styles.CarMakesList, className)}
            {...props}
          >
            <HeadingLevels semanticLevel={3} styleLevel={3} className={styles.title}>
              {title.value}
            </HeadingLevels>
            <ul className={styles.listWrapper}>
              {carMakesList.map(({ href, name }) => (
                <li
                  key={href}
                  className={classNames(styles.listItem, showAllMakes && styles['-show-all-makes'])}
                >
                  <SimpleLink className={styles.carLink} href={`/${href}`} text={name} />
                </li>
              ))}
            </ul>
            {!showAllMakes && (
              <Link
                className={classNames(styles.showAllMakesBtn)}
                type="M"
                href={viewAllLink.url}
                purpleIcon
                iconId="arrows/arrow-up-right"
              >
                {viewAllLink.text}
              </Link>
            )}
          </Card>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};

CarMakesList.displayName = 'CarMakesList';
CarMakesList.schema = CarMakesListSchema;
